import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import ConfirmDialog from '@/components/ConfirmDialog';
import api from '@/lib/api';
import { readPageCache, writePageCache } from '@/lib/pageCache';
import useRefreshOnFocus from '@/lib/useRefreshOnFocus';
import { DEFAULT_PRODUCTION_PRIORITY, PRODUCTION_PRIORITIES } from '@/constants/production';
import '@/pages/Products.css';
import '@/pages/Clients.css';

const formatMoney = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR') + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
};

const ApprovedBudgetsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const cachedData = readPageCache('approved-budgets');
  const cachedProductionOrders = cachedData?.productionOrders || [];
  const [budgets, setBudgets] = useState(cachedData?.budgets || []);
  const [workers, setWorkers] = useState(cachedData?.workers || []);
  const [productionBudgetIds, setProductionBudgetIds] = useState(new Set(cachedProductionOrders.map(order => order.budget_id)));
  const [productionOrdersByBudget, setProductionOrdersByBudget] = useState(
    cachedProductionOrders.reduce((acc, order) => {
      acc[order.budget_id] = order;
      return acc;
    }, {})
  );
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [openClients, setOpenClients] = useState({});
  const [productionDialogBudget, setProductionDialogBudget] = useState(null);
  const [productionWorkerId, setProductionWorkerId] = useState('');
  const [productionPriority, setProductionPriority] = useState(DEFAULT_PRODUCTION_PRIORITY);
  const [sendingProduction, setSendingProduction] = useState(false);
  const [pendingDialogBudget, setPendingDialogBudget] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setError('');
      const [budgetsRes, productionRes, workersRes] = await Promise.all([
        api.get('/budgets?status=approved'),
        api.get('/production-orders'),
        api.get('/workers'),
      ]);
      setBudgets(budgetsRes.data);
      setWorkers(workersRes.data || []);
      const productionOrders = productionRes.data || [];
      setProductionBudgetIds(new Set(productionOrders.map(order => order.budget_id)));
      setProductionOrdersByBudget(
        productionOrders.reduce((acc, order) => {
          acc[order.budget_id] = order;
          return acc;
        }, {})
      );
      writePageCache('approved-budgets', {
        budgets: budgetsRes.data,
        productionOrders,
        workers: workersRes.data || [],
      });
    } catch (err) {
      setError('Error al cargar presupuestos aprobados.');
      setBudgets([]);
      setProductionBudgetIds(new Set());
      setProductionOrdersByBudget({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useRefreshOnFocus(load);

  const handleMoveToPending = async (budget) => {
    setPendingDialogBudget(budget);
  };

  const confirmMoveToPending = async () => {
    if (!pendingDialogBudget) return;
    try {
      setPendingLoading(true);
      await api.put(`/budgets/${pendingDialogBudget.id}/status`, { status: 'pending' });
      setPendingDialogBudget(null);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error al actualizar presupuesto');
    } finally {
      setPendingLoading(false);
    }
  };

  const handleSendToProduction = async (budget) => {
    setProductionDialogBudget(budget);
    setProductionWorkerId(workers[0]?.id || '');
    setProductionPriority(DEFAULT_PRODUCTION_PRIORITY);
  };

  const confirmSendToProduction = async () => {
    if (!productionDialogBudget) return;
    if (!productionWorkerId) {
      alert('Selecciona un trabajador');
      return;
    }
    try {
      setSendingProduction(true);
      const { data } = await api.post(`/budgets/${productionDialogBudget.id}/send-to-production`, {
        assigned_worker_id: productionWorkerId,
        priority: productionPriority,
      });
      let productionOrder = data;
      if (productionOrder?.id && productionOrder.priority !== productionPriority) {
        const updateRes = await api.put(`/production-orders/${productionOrder.id}`, {
          priority: productionPriority,
        });
        productionOrder = updateRes.data;
      }
      setProductionBudgetIds(current => new Set([...current, productionDialogBudget.id]));
      setProductionOrdersByBudget(current => ({
        ...current,
        [productionDialogBudget.id]: productionOrder,
      }));
      setProductionDialogBudget(null);
      setProductionWorkerId('');
      setProductionPriority(DEFAULT_PRODUCTION_PRIORITY);
      navigate('/produccion');
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Error al enviar a produccion');
    } finally {
      setSendingProduction(false);
    }
  };

  const groupedByClient = useMemo(() => {
    const groups = new Map();
    const query = search.trim().toLowerCase();
    budgets
      .filter((budget) => {
        if (!query) return true;
        const clientName = budget.client_snapshot?.name || '';
        const productName = budget.product_snapshot?.name || '';
        return (
          clientName.toLowerCase().includes(query)
          || productName.toLowerCase().includes(query)
        );
      })
      .forEach((budget) => {
      const key = budget.client_id || budget.client_snapshot?.name || 'sin-cliente';
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          clientName: budget.client_snapshot?.name || 'Sin cliente',
          clientEmail: budget.client_snapshot?.email || '',
          clientPhone: budget.client_snapshot?.phone || '',
          budgets: [],
          total: 0,
        });
      }
      const group = groups.get(key);
      group.budgets.push(budget);
      group.total += Number(budget.final_price) || 0;
    });
    return Array.from(groups.values()).sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [budgets, search]);

  useEffect(() => {
    setOpenClients(current => {
      const next = {};
      groupedByClient.forEach((group) => {
        next[group.key] = current[group.key] ?? false;
      });
      return next;
    });
  }, [groupedByClient]);

  const toggleClient = (clientKey) => {
    setOpenClients(current => ({
      ...current,
      [clientKey]: !current[clientKey],
    }));
  };

  return (
    <div className="dashboard-container">
      <Sidebar activePath={location.pathname} />

      <main className="main-content">
        <div className="page-header-row">
          <div>
            <h1 data-testid="approved-budgets-title">Presupuestos Aprobados</h1>
            <p className="dashboard-subtitle">
              Trabajos confirmados por cliente
            </p>
          </div>
        </div>

        {error && <div className="login-error" data-testid="approved-budgets-error">{error}</div>}

        <div className="card approved-search-card">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Buscar</label>
            <input
              className="form-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cliente o producto"
              data-testid="approved-client-search"
            />
          </div>
        </div>

        {loading ? (
          <div className="empty-state">Cargando presupuestos aprobados...</div>
        ) : budgets.length === 0 ? (
          <div className="card">
            <div className="empty-state" data-testid="empty-approved-budgets">
              <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>No hay presupuestos aprobados</p>
              <p style={{ fontSize: '0.875rem', color: '#A0A0A0' }}>
                Cuando un cliente acepte un presupuesto, aprobalo desde el detalle o la lista de presupuestos.
              </p>
            </div>
          </div>
        ) : groupedByClient.length === 0 ? (
          <div className="card">
            <div className="empty-state" data-testid="empty-approved-search">
              No hay presupuestos aprobados que coincidan con la busqueda.
            </div>
          </div>
        ) : (
          groupedByClient.map(group => (
            <div className="card editor-card approved-client-card" key={group.key}>
              <button
                type="button"
                className="approved-client-header"
                onClick={() => toggleClient(group.key)}
                data-testid={`approved-client-toggle-${group.key}`}
              >
                <span className="approved-client-chevron">{openClients[group.key] ? 'v' : '>'}</span>
                <span className="approved-client-main">
                  <h3 className="card-title-inline">{group.clientName}</h3>
                </span>
                <span className="mono cell-strong approved-client-total">{formatMoney(group.total)}</span>
              </button>

              {openClients[group.key] && (
                <table className="data-table approved-budget-table">
                  <thead>
                    <tr>
                      <th style={{ width: '80px' }}>Nro.</th>
                      <th>Producto</th>
                      <th>Fecha aprobado</th>
                      <th style={{ textAlign: 'right' }}>Precio Final</th>
                      <th style={{ width: '230px', textAlign: 'right' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.budgets.map(budget => (
                      <tr key={budget.id} data-testid={`approved-budget-row-${budget.id}`}>
                        <td className="cell-strong mono">#{String(budget.budget_number).padStart(4, '0')}</td>
                        <td>{budget.product_snapshot?.name || '-'}</td>
                        <td>{formatDate(budget.approved_at || budget.updated_at || budget.created_at)}</td>
                        <td style={{ textAlign: 'right' }} className="mono cell-strong">{formatMoney(budget.final_price)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <Link
                            to={`/presupuestos/${budget.id}`}
                            className="btn-link"
                            data-testid={`view-approved-budget-${budget.id}`}
                          >
                            Ver
                          </Link>
                          {productionBudgetIds.has(budget.id) ? (
                            <Link
                              className={`production-sent-check ${productionOrdersByBudget[budget.id]?.status === 'entregado' ? 'delivered' : ''}`}
                              title={productionOrdersByBudget[budget.id]?.status === 'entregado' ? 'Ver orden entregada' : 'Ver orden de produccion'}
                              to={`/produccion/${productionOrdersByBudget[budget.id]?.id}`}
                            >
                              {productionOrdersByBudget[budget.id]?.status === 'entregado' ? '✓ Entregado' : '✓ Produccion'}
                            </Link>
                          ) : (
                            <button
                              className="btn-link"
                              onClick={() => handleSendToProduction(budget)}
                              data-testid={`send-production-budget-${budget.id}`}
                            >
                              Produccion
                            </button>
                          )}
                          {!productionBudgetIds.has(budget.id) && (
                            <button
                              className="btn-link"
                              onClick={() => handleMoveToPending(budget)}
                              data-testid={`pending-budget-${budget.id}`}
                            >
                              Pendiente
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))
        )}

        {productionDialogBudget && (
          <div className="modal-backdrop" onClick={() => setProductionDialogBudget(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Enviar a produccion</h3>
                <button className="btn-remove" onClick={() => setProductionDialogBudget(null)}>x</button>
              </div>
              <div className="modal-body">
                <p className="dashboard-subtitle" style={{ marginBottom: '1rem' }}>
                  Presupuesto #{String(productionDialogBudget.budget_number).padStart(4, '0')} - {productionDialogBudget.product_snapshot?.name || '-'}
                </p>
                <div className="form-group">
                  <label className="form-label">Trabajador *</label>
                  <select
                    className="form-input"
                    value={productionWorkerId}
                    onChange={(e) => setProductionWorkerId(e.target.value)}
                    autoFocus
                  >
                    <option value="">Seleccionar trabajador</option>
                    {workers.map(worker => (
                      <option key={worker.id} value={worker.id}>{worker.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Prioridad</label>
                  <select
                    className="form-input"
                    value={productionPriority}
                    onChange={(e) => setProductionPriority(e.target.value)}
                  >
                    {PRODUCTION_PRIORITIES.map(priority => (
                      <option key={priority.key} value={priority.key}>{priority.label}</option>
                    ))}
                  </select>
                </div>
                {workers.length === 0 && (
                  <div className="login-error">Primero crea un trabajador en Configuracion.</div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setProductionDialogBudget(null)}>
                  Cancelar
                </button>
                <button
                  className="btn-primary-inline"
                  onClick={confirmSendToProduction}
                  disabled={sendingProduction || !productionWorkerId}
                >
                  {sendingProduction ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </div>
          </div>
        )}
        <ConfirmDialog
          open={!!pendingDialogBudget}
          title="Volver a pendiente"
          message={pendingDialogBudget ? `¿Estas seguro de devolver el presupuesto #${pendingDialogBudget.budget_number} a pendientes?` : ''}
          confirmLabel="Volver a pendiente"
          loading={pendingLoading}
          onConfirm={confirmMoveToPending}
          onCancel={() => setPendingDialogBudget(null)}
        />
      </main>
    </div>
  );
};

export default ApprovedBudgetsList;
